import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore session from localStorage on mount
    const storedUser = localStorage.getItem('bridgecare_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem('bridgecare_user');
      }
    }
    setLoading(false);
  }, []);

  const generateMRN = () => {
    // Generate Medical Record Number in format: MRN-YYYYMMDD-XXXXX
    const date = new Date();
    const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '');
    const random = Math.random().toString(36).substr(2, 5).toUpperCase();
    return `MRN-${dateStr}-${random}`;
  };

  const login = (email, password, role) => {
    // Mock login - accept any email/password
    const mockUser = {
      id: Math.random().toString(36).substr(2, 9),
      email,
      role,
      name: email.split('@')[0],
      mrn: role === 'patient' ? generateMRN() : undefined,
      createdAt: new Date().toISOString(),
      requiresPasswordChange: false, // Will be true for nurse-created accounts
    };
    
    setUser(mockUser);
    localStorage.setItem('bridgecare_user', JSON.stringify(mockUser));
    return mockUser;
  };

  const signup = (email, password, role, name) => {
    // Mock signup - accept any email/password
    const mockUser = {
      id: Math.random().toString(36).substr(2, 9),
      email,
      role,
      name: name || email.split('@')[0],
      mrn: role === 'patient' ? generateMRN() : undefined,
      createdAt: new Date().toISOString(),
      requiresPasswordChange: false,
    };
    
    setUser(mockUser);
    localStorage.setItem('bridgecare_user', JSON.stringify(mockUser));
    return mockUser;
  };

  const updatePassword = (newPassword) => {
    // Mock password update
    const updatedUser = {
      ...user,
      requiresPasswordChange: false,
    };
    setUser(updatedUser);
    localStorage.setItem('bridgecare_user', JSON.stringify(updatedUser));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('bridgecare_user');
  };

  const value = {
    user,
    login,
    signup,
    logout,
    updatePassword,
    loading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
